import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { quotedIlikePattern } from "@/lib/utils/postgrest-filters";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { normalizeAllowedBehaviors } from "@/lib/appointments/availability";
import type { DoseIntervalUnit } from "@/lib/dose-interval";

/**
 * Converts empty or whitespace-only string to null so Postgres UNIQUE (customer_id, sku)
 * allows multiple rows with NULL sku (NULL is distinct from NULL in unique checks).
 */
function normalizeEmptyToNull(
  value: string | undefined | null
): string | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
}

export interface ItemDoseInterval {
  id: string;
  item_id: string;
  next_application_item_id: string | null;
  dose_interval_value: number | null;
  dose_interval_unit: DoseIntervalUnit | null;
  sort_order: number;
}

export interface Item {
  id: string;
  type: "PRODUTO" | "SERVICO" | "PACOTE";
  name: string;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  brand_id: string | null;
  unit: string;
  cost_price: number;
  sale_price: number;
  description: string | null;
  is_active: boolean;
  track_by_batch: boolean;
  track_by_expiry: boolean;
  service_duration_minutes: number | null;
  manufacturer: string | null;
  dose_ml: number | null;
  storage_temp: string | null;
  is_perishable: boolean | null;
  product_subtype_id: string | null;
  product_subtype?: { name: string } | null;
  package_subtype_id: string | null;
  package_subtype?: { name: string } | null;
  registration_number: string | null;
  xml_import_id: string | null;
  service_group_id: string | null;
  created_at: string;
  deleted_at: string | null;
  deleted_by: string | null;
  allowed_appointment_types: string[];
  dose_intervals: ItemDoseInterval[];
}

export interface ItemInput {
  type: "PRODUTO" | "SERVICO" | "PACOTE";
  name: string;
  sku?: string;
  barcode?: string;
  category_id?: string;
  brand_id?: string;
  unit?: string;
  cost_price?: number;
  sale_price?: number;
  markup_percentage?: number;
  description?: string;
  is_active?: boolean;
  track_by_batch?: boolean;
  track_by_expiry?: boolean;
  service_duration_minutes?: number;
  is_perishable?: boolean;
  product_subtype_id?: string | null;
  package_subtype_id?: string | null;
  manufacturer?: string;
  dose_ml?: number;
  storage_temp?: string;
  registration_number?: string;
  xml_import_id?: string;
  service_group_id?: string | null;
  allowed_appointment_types?: string[];
}

export interface ItemsFilters {
  type?: string;
  excludeTypes?: Array<Item["type"]>;
  search?: string;
  isActive?: boolean;
  productSubtypeId?: string;
  packageSubtypeId?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedItems {
  data: Item[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

type ItemAllowedAppointmentTypeRow = {
  appointment_type?: string | null;
};

type RawItem = Omit<Item, "allowed_appointment_types" | "dose_intervals"> & {
  allowed_appointment_types?: ItemAllowedAppointmentTypeRow[] | null;
  dose_intervals?: Array<{
    id: string;
    item_id: string;
    next_application_item_id: string | null;
    dose_interval_value: number | null;
    dose_interval_unit: string | null;
    sort_order: number;
  }> | null;
};

const ITEMS_SELECT = "*, product_subtype:product_subtypes(name), package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type), dose_intervals:item_dose_intervals!item_dose_intervals_item_id_fkey(id, item_id, next_application_item_id, dose_interval_value, dose_interval_unit, sort_order)";

const normalizeItem = (item: RawItem): Item => ({
  ...item,
  allowed_appointment_types: normalizeAllowedBehaviors(
    item.allowed_appointment_types?.map((row) => row.appointment_type ?? null) ?? [],
  ),
  dose_intervals: (item.dose_intervals ?? [])
    .map((row) => ({
      ...row,
      dose_interval_unit: row.dose_interval_unit as DoseIntervalUnit | null,
    }))
    .sort((a, b) => a.sort_order - b.sort_order),
});

const syncAllowedAppointmentTypes = async (itemId: string, allowedAppointmentTypes?: string[]) => {
  const normalizedTypes = normalizeAllowedBehaviors(allowedAppointmentTypes);

  const { error: deleteError } = await supabase
    .from("item_allowed_appointment_types")
    .delete()
    .eq("item_id", itemId);

  if (deleteError) throw deleteError;

  if (normalizedTypes.length === 0) return;

  const { error: insertError } = await supabase
    .from("item_allowed_appointment_types")
    .insert(normalizedTypes.map((appointmentType) => ({
      item_id: itemId,
      appointment_type: appointmentType,
    })));

  if (insertError) throw insertError;
};

const toItemPayload = (input: ItemInput) => {
  const {
    type, name, sku, barcode, category_id, brand_id, unit, cost_price, sale_price,
    markup_percentage, description, is_active, track_by_batch, track_by_expiry,
    service_duration_minutes, is_perishable, product_subtype_id, package_subtype_id,
    manufacturer, dose_ml, storage_temp, registration_number, xml_import_id,
    service_group_id
  } = input;

  return {
    type, name,
    sku: normalizeEmptyToNull(sku),
    barcode: normalizeEmptyToNull(barcode),
    category_id, brand_id, unit, cost_price, sale_price, markup_percentage, description,
    is_active, track_by_batch, track_by_expiry, service_duration_minutes, is_perishable,
    product_subtype_id, package_subtype_id, manufacturer, dose_ml, storage_temp,
    registration_number, xml_import_id,
    service_group_id: service_group_id ?? null,
  };
};

export function useItems(filters?: ItemsFilters, enabled = true) {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery<PaginatedItems | Item[]>({
    queryKey: ["items", customerId, filters],
    enabled: !!customerId && enabled,
    queryFn: async () => {
      if (!customerId) throw new Error("Nenhuma clínica selecionada");
      const page = filters?.page ?? 1;
      const pageSize = filters?.pageSize ?? 50;
      const offset = (page - 1) * pageSize;

      let query = supabase
        .from("items")
        .select(ITEMS_SELECT, { count: "exact" })
        .eq("customer_id", customerId)
        .is("deleted_at", null)
        .order("name");

      if (filters?.type) {
        const normalizedType = filters.type.toUpperCase() as Item["type"];
        query = query.eq("type", normalizedType);
      }
      if (filters?.excludeTypes?.length) {
        query = query.not("type", "in", `(${filters.excludeTypes.join(",")})`);
      }
      if (filters?.isActive !== undefined) {
        query = query.eq("is_active", filters.isActive);
      }
      if (filters?.productSubtypeId) {
        query = query.eq("product_subtype_id", filters.productSubtypeId);
      }
      if (filters?.packageSubtypeId) {
        query = query.eq("package_subtype_id", filters.packageSubtypeId);
      }
      if (filters?.search) {
        const p = quotedIlikePattern(filters.search);
        query = query.or(`name.ilike.${p},barcode.ilike.${p},sku.ilike.${p}`);
      }

      // Apply pagination if page and pageSize are provided
      if (filters?.page !== undefined && filters?.pageSize !== undefined) {
        query = query.range(offset, offset + pageSize - 1);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      // Return paginated response if pagination is requested
      if (filters?.page !== undefined && filters?.pageSize !== undefined) {
        return {
          data: ((data || []) as unknown as RawItem[]).map(normalizeItem),
          totalCount: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize),
          currentPage: page,
          pageSize,
        } as PaginatedItems;
      }

      // Return simple array for backward compatibility
      return ((data || []) as unknown as RawItem[]).map(normalizeItem);
    },
  });
}

export function useItem(id: string) {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["items", customerId, id],
    enabled: !!id && !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error("Nenhuma clínica selecionada");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("items")
        .select(ITEMS_SELECT)
        .eq("id", id)
        .eq("customer_id", customerId)
        .is("deleted_at", null)
        .single();
      if (error) throw error;
      return normalizeItem(data as RawItem);
    },
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ItemInput) => {
      // Add customer_id for multi-tenancy
      const customerId = await getCurrentClinicCustomerId();
      const { allowed_appointment_types } = input;

      // Calculate sale_price from cost_price and markup if provided
      const finalInput = toItemPayload(input);
      if (finalInput.cost_price && finalInput.markup_percentage && !finalInput.sale_price) {
        finalInput.sale_price = finalInput.cost_price * (1 + finalInput.markup_percentage / 100);
      }

      const payload = {
        ...finalInput,
        customer_id: customerId,
      };

      const { data, error } = await (supabase as typeof supabase & {
        from: (table: "items") => {
          insert: (value: unknown) => {
            select: () => {
              single: () => Promise<{ data: RawItem; error: Error | null }>;
            };
          };
        };
      })
        .from("items")
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      await syncAllowedAppointmentTypes(data.id, allowed_appointment_types);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["applications-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      toast.success("Item criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar item: ${error.message}`);
    },
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ItemInput }) => {
      const { allowed_appointment_types } = input;
      const normalizedInput = toItemPayload(input);
      const { data, error } = await (supabase as typeof supabase & {
        from: (table: "items") => {
          update: (value: unknown) => {
            eq: (column: string, value: string) => {
              select: () => {
                single: () => Promise<{ data: RawItem; error: Error | null }>;
              };
            };
          };
        };
      })
        .from("items")
        .update(normalizedInput)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      await syncAllowedAppointmentTypes(id, allowed_appointment_types);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["items", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["applications-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      toast.success("Item atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar item: ${error.message}`);
    },
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Use SECURITY DEFINER function to bypass RLS
      const { error } = await supabase.rpc("soft_delete_item", {
        p_item_id: id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["applications-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stock-movements"] });
      toast.success("Item excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir item: ${error.message}`);
    },
  });
}

export type UpsertDoseIntervalInput = {
  id?: string;
  next_application_item_id: string | null;
  dose_interval_value: number | null;
  dose_interval_unit: DoseIntervalUnit | null;
  sort_order: number;
};

export function useUpsertItemDoseIntervals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, intervals }: { itemId: string; intervals: UpsertDoseIntervalInput[] }) => {
      const { error: deleteError } = await supabase
        .from("item_dose_intervals")
        .delete()
        .eq("item_id", itemId);
      if (deleteError) throw deleteError;

      const validIntervals = intervals.filter(
        (row) => row.dose_interval_value !== null && row.dose_interval_unit !== null,
      );
      if (validIntervals.length === 0) return;

      const { error: insertError } = await supabase
        .from("item_dose_intervals")
        .insert(
          validIntervals.map((row, index) => ({
            item_id: itemId,
            next_application_item_id: row.next_application_item_id || null,
            dose_interval_value: row.dose_interval_value,
            dose_interval_unit: row.dose_interval_unit,
            sort_order: index,
          })),
        );
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["applications-catalog"] });
    },
  });
}
